import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Route, Layers, Navigation } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom icons
const createCustomIcon = (color, emoji) => {
  return L.divIcon({
    className: "custom-marker",
    html: `<div style="background: ${color}; width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 10px rgba(0,0,0,0.3); font-size: 18px;">${emoji}</div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -18],
  });
};

const destinationIcons = {
  city: createCustomIcon("#C68E5D", "🏛️"),
  village: createCustomIcon("#8B7355", "🏘️"),
  dunes: createCustomIcon("#E76F51", "🏜️"),
  kasbah: createCustomIcon("#9B2335", "🏰"),
  valley: createCustomIcon("#2D5016", "🌴"),
  gorge: createCustomIcon("#4A4A4A", "⛰️"),
};

const MapPage = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const location = useLocation();
  const [routes, setRoutes] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRoutes, setShowRoutes] = useState(true);

  // Get language prefix from current URL
  const getLocalizedPath = (path) => {
    const langMatch = location.pathname.match(/^\/(fr|en|ar|nl|es)/);
    const currentLang = langMatch ? langMatch[1] : lang || 'fr';
    return `/${currentLang}${path}`;
  };
  const [showDestinations, setShowDestinations] = useState(true);

  useEffect(() => {
    const fetchMapData = async () => {
      try {
        const [routesRes, destRes] = await Promise.all([
          axios.get(`${API}/map/routes`),
          axios.get(`${API}/map/destinations`),
        ]);
        setRoutes(routesRes.data);
        setDestinations(destRes.data);
      } catch (error) {
        console.error("Error fetching map data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMapData();
  }, []);

  const routeColors = {
    "4x4": "#E76F51",
    trekking: "#C68E5D",
  };

  // Center of Morocco Sahara region
  const mapCenter = [30.0, -6.0];

  return (
    <div className="min-h-screen pt-20" data-testid="map-page">
      {/* Header */}
      <div className="bg-muted py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-6">
            <span className="text-primary font-medium tracking-widest uppercase text-sm">
              {t("map.subtitle")}
            </span>
            <h1 className="font-serif text-3xl md:text-5xl font-bold mt-2">
              {t("map.title")}
            </h1>
            <p className="text-muted-foreground mt-4 max-w-3xl mx-auto text-lg leading-relaxed">
              {t("map.intro")}
            </p>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap items-center justify-center gap-6">
            <div className="flex items-center gap-2">
              <Switch
                id="show-routes"
                checked={showRoutes}
                onCheckedChange={setShowRoutes}
                data-testid="toggle-routes"
              />
              <Label htmlFor="show-routes" className="flex items-center gap-2">
                <Route className="h-4 w-4 text-primary" />
                {t("map.routes")}
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="show-destinations"
                checked={showDestinations}
                onCheckedChange={setShowDestinations}
                data-testid="toggle-destinations"
              />
              <Label htmlFor="show-destinations" className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                {t("map.destinations")}
              </Label>
            </div>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="relative">
          {loading && (
            <div className="absolute inset-0 bg-muted rounded-2xl flex items-center justify-center z-[1000]">
              <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full" />
            </div>
          )}
          <div
            className="w-full h-[600px] rounded-2xl overflow-hidden shadow-lg"
            data-testid="map-container"
          >
            <MapContainer
              center={mapCenter}
              zoom={6}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              {/* Routes */}
              {showRoutes && routes.map((route) => (
                <Polyline
                  key={route.id}
                  positions={route.coordinates.map(coord => [coord[1], coord[0]])}
                  pathOptions={{
                    color: routeColors[route.type] || "#C68E5D",
                    weight: 4,
                    opacity: 0.8,
                    dashArray: route.type === "trekking" ? "10, 10" : undefined,
                  }}
                >
                  <Popup>
                    <div className="p-2 min-w-[200px]">
                      <h3 className="font-bold text-lg mb-1">
                        {route.title[lang] || route.title.fr}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">
                        {route.type === "4x4" ? "Circuit 4x4" : "Trek à pied"}
                      </p>
                      <p className="text-sm text-gray-600">
                        Départ: {route.departure_city}
                      </p>
                      <Link to={getLocalizedPath(`/circuit/${route.id}`)}>
                        <Button size="sm" className="mt-2 w-full">
                          Voir le circuit
                        </Button>
                      </Link>
                    </div>
                  </Popup>
                </Polyline>
              ))}

              {/* Destinations */}
              {showDestinations && destinations.map((dest, index) => (
                <Marker
                  key={index}
                  position={[dest.coordinates[1], dest.coordinates[0]]}
                  icon={destinationIcons[dest.type] || destinationIcons.city}
                >
                  <Popup>
                    <div className="p-2">
                      <h3 className="font-bold text-lg">{dest.name}</h3>
                      <p className="text-sm text-gray-600">{t(`map.${dest.type}`)}</p>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-8 p-6 bg-card rounded-xl border border-border">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <Layers className="h-5 w-5 text-primary" />
            Légende
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-1 bg-accent rounded" />
              <span className="text-sm">Circuit 4x4</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-1 bg-primary rounded" style={{ borderTop: "2px dashed #C68E5D" }} />
              <span className="text-sm">Trek</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xl">🏜️</span>
              <span className="text-sm">{t("map.dunes")}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xl">🏛️</span>
              <span className="text-sm">{t("map.city")}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xl">🏰</span>
              <span className="text-sm">{t("map.kasbah")}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xl">🌴</span>
              <span className="text-sm">{t("map.valley")}</span>
            </div>
          </div>
        </div>

        {/* Routes List */}
        <div className="mt-8">
          <h3 className="font-serif text-2xl font-semibold mb-4">{t("map.routes")}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {routes.map((route) => (
              <Link
                key={route.id}
                to={getLocalizedPath(`/circuit/${route.id}`)}
                className="p-4 bg-card rounded-xl border border-border hover:border-primary transition-colors"
                data-testid={`route-card-${route.id}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={route.type === "4x4" ? "bg-accent" : "bg-primary"}>
                    {route.type}
                  </Badge>
                </div>
                <h4 className="font-semibold">{route.title[lang] || route.title.fr}</h4>
                <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                  <Navigation className="h-3 w-3" />
                  {route.departure_city}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapPage;
